import { useState } from 'react';
import { motion } from 'framer-motion';
import { fadeIn, fadeInUp } from '../utils/animations';
import FeaturePill from './FeaturePill';
import { useKickstarterForm } from '../hooks/useKickstarterForm';

const Hero = () => {
  // Use the actual form hook for real API integration
  const { form, onSubmit } = useKickstarterForm();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = form;
  
  const [formStatus, setFormStatus] = useState<{success?: boolean; message?: string}>({});
  
  // Submit handler that actually connects to the proper API
  const submitForm = async (data: { email: string }) => {
    try {
      console.log('Hero form submitting:', data);
      const result = await onSubmit(data);
      
      if (result.success) {
        setFormStatus({
          success: true,
          message: result.message || 'Thanks for signing up! We\'ll notify you when our Kickstarter launches.'
        });
        form.reset();
      } else {
        setFormStatus({
          success: false,
          message: result.error || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      console.error('Hero form error:', error);
      setFormStatus({
        success: false,
        message: 'An unexpected error occurred. Please try again later.'
      });
    }
  };

  return (
    <div className="relative overflow-hidden bg-slate-900 pt-0">
      {/* Background gradient effect */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-slate-900 to-slate-900" />
      </div>
      
      <div className="relative w-full pt-0 pb-12">
        <div className="container px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            <motion.div 
              className="flex flex-col justify-center"
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <div className="mb-6">
                <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-white mb-5">
                  The All-In-One Fantasy Miniature Painting Kit
                </h1>
                <p className="text-lg md:text-xl text-slate-300 mb-6 max-w-xl">
                  Everything you need to paint stunning, unique fantasy miniatures – perfect for beginners and RPG gamers. Sign up for launch alerts & early bird deals!
                </p>
                
                <div className="bg-slate-800/70 p-6 rounded-lg border border-slate-700 mb-6">
                  {formStatus.success ? (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-4"
                    >
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-900/50 text-green-400 mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-green-400 font-medium">{formStatus.message}</p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit(submitForm)}>
                      <p className="text-lg font-medium text-white mb-3">Get Kickstarter Alerts & Early Access!</p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="email"
                          placeholder="Your email address"
                          className={`flex-grow px-4 py-3 bg-slate-700 border ${errors.email ? 'border-red-500' : 'border-slate-600'} rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500`}
                          {...register('email')}
                        />
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn-primary whitespace-nowrap"
                        >
                          {isSubmitting ? 'Submitting...' : 'Notify Me On Launch!'}
                        </button>
                      </div>
                      {errors.email && (
                        <p className="mt-2 text-red-400 text-sm">{errors.email.message as string}</p>
                      )}
                      {formStatus.message && !formStatus.success && (
                        <p className="mt-2 text-red-400 text-sm">{formStatus.message}</p>
                      )}
                      {import.meta.env.DEV && (
                        <div className="mt-2 p-1 border border-yellow-700 bg-yellow-900/30 rounded-md">
                          <p className="text-yellow-400 text-xs">Debug: Form will submit to API endpoint</p>
                        </div>
                      )}
                    </form>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-4 md:gap-6">
                  <FeaturePill text="8 Unique Miniatures Per Box" />
                  <FeaturePill text="Premium Paints & Brushes" />
                  <FeaturePill text="Step-by-Step Guides" />
                  <FeaturePill text="Legendary Adventures" />
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="lg:order-last"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              <div className="flex flex-col items-center">
                <img 
                  src="/assets/Legendary Brushstrokes Logo.png" 
                  alt="Legendary Brushstrokes" 
                  className="w-full mb-4"
                />
                <div className="relative">
                  <div className="overflow-hidden rounded-lg shadow-xl shadow-purple-900/40">
                    <img
                      className="w-full h-auto"
                      src="/assets/LegendaryBrushstrokes - Elemental Set-fixed.jpg"
                      alt="Legendary Brushstrokes Elemental Set"
                    />
                  </div>
                
                  {/* Kickstarter badge */}
                  <div className="absolute -top-12 -right-12 -md:top-12 -md:right-12">
                    <div className="relative">
                      <div className="absolute inset-0 bg-white rounded-full blur-sm opacity-20"></div>
                      <div className="relative bg-slate-800 rounded-full p-2 border-2 border-white/20">
                          <img 
                            src="/assets/ks-logo.png" 
                            alt="Kickstarter" 
                            className="w-32 h-32" 
                          />
                      </div>
                    </div>
                  </div>
                  
                  {/* Coming soon badge */}
                  <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-purple-700 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                      Coming Soon on Kickstarter
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
